import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Form } from 'react-bootstrap';
import { getModificationReasons } from '../../../../../../../crud/pedidos_api/pedidos_modifications';
import { toast } from 'react-toastify';
import './ModificationReasonModal.scss';

interface ModificationReasonModalProps {
  show: boolean;
  onClose: () => void;
  onConfirm: (payload: { reasonId?: number; comment?: string }) => Promise<void>;
  deletionsCount: number;
  willCloseMesa?: boolean; // Flag to indicate if confirming will close the mesa
  printToKitchen: boolean;
  onPrintToKitchenChange: (value: boolean) => void;
  autoPrintToKitchen?: boolean;
}

const ModificationReasonModal: React.FC<ModificationReasonModalProps> = ({
  show,
  onClose,
  onConfirm,
  deletionsCount,
  willCloseMesa = false,
  printToKitchen,
  onPrintToKitchenChange,
  autoPrintToKitchen = false,
}) => {
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [selectedReason, setSelectedReason] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasLoadedReasons, setHasLoadedReasons] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [customReasonText, setCustomReasonText] = useState('');

  // Fetch modification reasons when modal is shown
  useEffect(() => {
    if (show && !hasLoadedReasons) {
      setLoading(true);
      getModificationReasons()
        .then((response) => {
          setReasons(response.data);
          setHasLoadedReasons(true);
        })
        .catch((error) => {
          console.error('Error fetching modification reasons:', error);
          toast.error('Error al cargar las razones de modificación');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [show, hasLoadedReasons]);

  // Reset selection when modal opens
  useEffect(() => {
    if (show) {
      setSelectedReason(null);
      setCustomReasonText('');
    }
  }, [show]);

  const hasCustomReason = customReasonText.trim().length > 0;
  const hasSelectedReason = selectedReason !== null;
  const shouldShowReasonList = !hasCustomReason;

  const handleConfirm = async () => {
    if (!hasSelectedReason && !hasCustomReason) {
      toast.warning('Por favor selecciona una razón');
      return;
    }
    
    setIsConfirming(true);
    try {
      await onConfirm(
        hasCustomReason
          ? { comment: customReasonText.trim() }
          : { reasonId: selectedReason ?? undefined }
      );
    } catch (error) {
      console.error('Error confirming modifications:', error);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onClose}
      centered
      className="modification-reason-modal"
    >
      <Modal.Header>
        <div className="form-modal-title-container">
          <h2 className="form-modal-title">Razón de Eliminación</h2>
        </div>
        <button 
          type="button" 
          className="modal-close-button" 
          onClick={onClose}
          aria-label="Close"
        >
          <span aria-hidden="true">×</span>
        </button>
      </Modal.Header>
      <Modal.Body>
        <p className="deletion-count">
          Se {deletionsCount === 1 ? 'eliminará' : 'eliminarán'} {deletionsCount} {deletionsCount === 1 ? 'producto' : 'productos'}.
          {willCloseMesa && (
            <>
              <br />
              <strong style={{ color: '#ff6b6b', marginTop: '0.5rem', display: 'inline-block' }}>
                ⚠️ Al eliminar este producto, la mesa se cerrará y volverá a estar disponible.
              </strong>
            </>
          )}
          <br />
          Por favor selecciona una  o escriba una razón:
        </p>

        {loading ? (
          <div className="loading-reasons">Cargando razones...</div>
        ) : (
          //I want to add the option for the user to write a custom reason if none of the predefined reasons fit. This should be a text input that appears when the user selects an option like "Otra razón" (which I will add to the backend reasons list). When "Otra razón" is selected, the text input should become enabled and allow the user to type their custom reason. The custom reason should then be sent to the backend when confirming, instead of the predefined reason ID.
          
          <div className="reasons-list">
            <Form.Group className="mt-3">
              <Form.Label>
                Comentario {shouldShowReasonList ? '' : <span className="comment-required">*</span>}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={customReasonText}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setCustomReasonText(nextValue);
                  if (nextValue.trim().length > 0) {
                    setSelectedReason(null);
                  }
                }}
                onFocus={() => setSelectedReason(null)}
                placeholder="Escriba un comentario o seleccione una razón para la cancelacion del producto."
                maxLength={500}
                className="reset-table-comment-textarea"
              />
            </Form.Group>

            {shouldShowReasonList && Object.entries(reasons).map(([id, description]) => (
              <div 
                key={id}
                className={`reason-item ${selectedReason === Number(id) ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedReason(Number(id));
                  setCustomReasonText('');
                }}
              >
                <input
                  type="radio"
                  name="modification-reason"
                  id={`reason-${id}`}
                  checked={selectedReason === Number(id)}
                  onChange={() => {
                    setSelectedReason(Number(id));
                    setCustomReasonText('');
                  }}
                />
                <label htmlFor={`reason-${id}`}>
                  {description}
                </label>
              </div>
            ))}
          </div>
        )}
        
        <div 
          className={`kitchen-notification-card ${autoPrintToKitchen || printToKitchen ? 'active' : ''}`}
          onClick={() => {
            if (!autoPrintToKitchen && !loading && !isConfirming) {
              onPrintToKitchenChange(!printToKitchen);
            }
          }}
        >
          <div className="notification-content">
              <div className="notification-icon">🔔</div>
              <div className="notification-text">
                <div className="notification-title">Notificar a cocina</div>
                <div className="notification-subtitle">
                  {autoPrintToKitchen
                    ? 'Se enviará automáticamente por configuración'
                    : 'Enviar comanda de modificación'}
                </div>
              </div>
            </div>
          {!autoPrintToKitchen && (
            <div className={`custom-switch ${printToKitchen ? 'checked' : ''}`}>
              <div className="switch-slider"></div>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button 
            variant="none" 
            className="form-button-cancel" 
            onClick={onClose}
            disabled={isConfirming}
          >
            Cancelar
          </Button>
          <Button 
            variant="none" 
            className="form-button-create"
            onClick={handleConfirm}
            disabled={isConfirming || (!hasSelectedReason && !hasCustomReason)}
          >
            {isConfirming ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  style={{ marginRight: '0.5rem' }}
                />
                Confirmando...
              </>
            ) : (
              'Confirmar Cambios'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ModificationReasonModal;
